import { KeyValue } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChildren,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BrandVarEntity, BrandVariable } from 'app/account/brand-variable';
import WidgetComponentHelpers from 'app/campaign/widget-component-helpers';
import ColorHelpers from 'app/shared/helpers/color-helpers';
import { TabsBrandSettings } from '../account-settings-brand-settings.component';
import { MatMenuTrigger } from '@angular/material/menu';

@Component({
  selector: 'app-brand-variables',
  templateUrl: './brand-variables.component.html',
  styleUrl: './brand-variables.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BrandVariablesComponent {
  @ViewChildren(MatMenuTrigger) menuTriggers: QueryList<MatMenuTrigger>;
  @Input() defaultFonts;
  @Input() customFonts;
  @Input() accountColors;
  @Input() isAutomaticallyGenerated;
  @Input() isBrandVarPageForTemplatePage;
  @Input() set setBrandVariables(brandVar) {
    if (brandVar) {
      this.setBrandVariablesByTab(brandVar.brandVariables, brandVar.initialBrandVariables, brandVar.type);
      this.setSideEffectsSettings();
    }
  }
  @Output() addEditCustomFont = new EventEmitter();
  @Output() saveBrandVariables = new EventEmitter();
  @Output() onChangeBrandVariables = new EventEmitter();
  @Output() brandVarCanBeSavedOnClickTab = new EventEmitter();
  @Output() onPreviewRefresh = new EventEmitter();
  public isBorderSet: boolean;
  public brandVariables: BrandVariable;
  public brandVariablesAll: BrandVariable;
  public initialBrandVariablesAll: BrandVariable;
  public initialBrandVariables: BrandVariable;
  public displayFontNames = {};
  public BrandVarEntity = BrandVarEntity;
  public brandVariablesErrors = {
    headline: { color: '', lineHeight: '', letterSpacing: '' },
    text: { color: '', lineHeight: '', letterSpacing: '' },
    button: {
      color: '',
      lineHeight: '',
      letterSpacing: '',
      radius: '',
      backgroundColor: '',
      height: '',
      border: { color: '', width: '' },
    },
    input: {
      color: '',
      lineHeight: '',
      letterSpacing: '',
      radius: '',
      backgroundColor: '',
      border: { color: '', width: '' },
    },
    label: {},
    canvas: { backgroundColor: '', closeButtonColor: '' },
  };

  constructor(private translateService: TranslateService) {}

  public setBrandVariablesByTab(brandVariables, initialBrandVariables, type): void {
    this.brandVariablesAll = brandVariables;
    this.initialBrandVariablesAll = initialBrandVariables;
    if (type === TabsBrandSettings.CampaignDesign) {
      this.brandVariables = this.brandVariablesGeneral(brandVariables) as BrandVariable;
      this.initialBrandVariables = this.brandVariablesGeneral(initialBrandVariables) as BrandVariable;
    } else {
      this.brandVariables = this.brandVariablesForm(brandVariables) as BrandVariable;
      this.initialBrandVariables = this.brandVariablesForm(initialBrandVariables) as BrandVariable;
    }
  }

  public brandVariablesGeneral(brandVariables): BrandVariable {
    if (brandVariables) {
      return {
        button: brandVariables.button,
        canvas: brandVariables.canvas,
        headline: brandVariables.headline,
        text: brandVariables.text,
      } as BrandVariable;
    }
  }

  public brandVariablesForm(brandVariables): BrandVariable {
    if (brandVariables) {
      return { input: brandVariables.input, label: brandVariables.label } as BrandVariable;
    }
  }

  get isBrandSettingsNotChanged() {
    if (JSON.stringify(this.brandVariablesAll) !== JSON.stringify(this.initialBrandVariablesAll)) {
      this.onChangeBrandVariables.emit(true);
    } else {
      this.onChangeBrandVariables.emit(false);
    }
    return JSON.stringify(this.brandVariables) === JSON.stringify(this.initialBrandVariables);
  }

  public keyDescOrder = (a: KeyValue<number | string, string>, b: KeyValue<number | string, string>): number => {
    if (a.key === 'canvas') return -1;
    if (b.key === 'canvas') return 1;
    if (a.key === 'headline') return -1;
    if (b.key === 'headline') return 1;
    return a.key > b.key ? -1 : b.key > a.key ? 1 : 0;
  };

  get isBrandSettingsError() {
    return Object.values(this.brandVariablesErrors).some((section) => Object.values(section).some((value) => value));
  }

  public setSideEffectsSettings(): void {
    for (const key in this.brandVariables) {
      if (Object.prototype.hasOwnProperty.call(this.brandVariables, key)) {
        const element = this.brandVariables[key];
        if (element.textProperties) {
          const displayName = WidgetComponentHelpers.getDefaultFonts().filter(
            (o) => o.name === element.textProperties.font,
          )[0];
          this.displayFontNames[key] = displayName || {
            displayName: element.textProperties.font,
            name: element.textProperties.font,
          };
        }
      }
      if (this.brandVariables[key].border) {
        this.isBorderSet = !!Number(this.brandVariables[key]?.border?.width);
      }
    }
  }

  public validateColorBrandVariables(color, typeVariable, typeProperty, innerProperty?): void {
    color = color.trim();
    if (innerProperty) this.brandVariablesErrors[typeVariable][typeProperty][innerProperty] = '';
    else this.brandVariablesErrors[typeVariable][typeProperty] = '';
    if (!color) {
      if (innerProperty) {
        this.brandVariablesErrors[typeVariable][typeProperty][innerProperty] = this.translateService.instant(
          'generic.validation.error-required',
        );
      } else {
        this.brandVariablesErrors[typeVariable][typeProperty] = this.translateService.instant(
          'generic.validation.error-required',
        );
      }
    } else {
      const isHexColor = ColorHelpers.validateHexColor(color);
      let isRgbaColor = false;
      if (!isHexColor) {
        isRgbaColor = ColorHelpers.validateRgbaColor(color);
      }
      if (!isHexColor && !isRgbaColor) {
        if (innerProperty) {
          this.brandVariablesErrors[typeVariable][typeProperty][innerProperty] = this.translateService.instant(
            'generic.validation.error-invalid-color-code',
          );
        } else {
          this.brandVariablesErrors[typeVariable][typeProperty] = this.translateService.instant(
            'generic.validation.error-invalid-color-code',
          );
        }
      }
    }
    this.setbrandVarCanBeSavedOnClickTab();
    this.setBrandVariablesPreview(typeVariable, typeProperty, innerProperty);
  }

  private setbrandVarCanBeSavedOnClickTab() {
    this.brandVarCanBeSavedOnClickTab.emit({
      brandVariablesErrors: this.brandVariablesErrors,
      isBrandSettingsNotChanged: this.isBrandSettingsNotChanged,
    });
  }

  public validateBrandVariables(value, typeVariable, typeProperty, innerProperty): void {
    if (innerProperty) {
      this.brandVariablesErrors[typeVariable][typeProperty][innerProperty] = '';
      if (value === '' || value < 0) {
        this.brandVariablesErrors[typeVariable][typeProperty][innerProperty] = this.translateService.instant(
          'generic.validation.error-wrong-number',
        );
      }
    } else {
      this.brandVariablesErrors[typeVariable][typeProperty] = '';
      if (value === '' || value < 0) {
        this.brandVariablesErrors[typeVariable][typeProperty] = this.translateService.instant(
          'generic.validation.error-wrong-number',
        );
      }
    }

    this.setbrandVarCanBeSavedOnClickTab();
    this.setBrandVariablesPreview(typeVariable, typeProperty, innerProperty);
  }

  private setBrandVariablesPreview(typeVariable, typeProperty, innerProperty?): void {
    for (const key in this.brandVariablesErrors) {
      const variable = this.brandVariablesErrors[key];
      if (key === typeVariable) {
        for (const prop in variable) {
          if (!innerProperty && !variable[prop] && prop === typeProperty) {
            this.refreshPreview();
            break;
          } else if (innerProperty && !variable[prop][innerProperty] && prop === typeProperty) {
            this.refreshPreview();
          }
        }
      }
    }
  }

  private refreshPreview() {
    this.onPreviewRefresh.emit(this.brandVariablesAll);
  }

  public onMenuFontClosed(textProperties): void {
    for (const key in this.brandVariablesErrors) {
      if (Object.prototype.hasOwnProperty.call(this.brandVariablesErrors, key)) {
        const element = this.brandVariablesErrors[key];
        for (const keyEl in element) {
          if (Object.prototype.hasOwnProperty.call(element, keyEl)) {
            const el = element[keyEl];
            if ([BrandVarEntity.LineHeight, BrandVarEntity.LetterSpacing].includes(keyEl as BrandVarEntity) && el) {
              textProperties[keyEl] = this.initialBrandVariables[key].textProperties[keyEl];
              element[keyEl] = '';
            }
          }
        }
      }
    }
    this.setbrandVarCanBeSavedOnClickTab();
    this.setSideEffectsSettings();
  }

  public changeOnColorPickerBrandVariable(color: string, itemVar, typeProperty, typeVariable, innerProperty?): void {
    const currentColor = ColorHelpers.getColorIfAccountVariable(color, this.accountColors);
    if (innerProperty) itemVar[innerProperty] = currentColor;
    else itemVar[typeProperty] = currentColor;
    this.validateColorBrandVariables(currentColor, typeVariable, typeProperty, innerProperty);
  }

  public emitUpdatesFontOnBrandVariables(emitedVal): void {
    this.setbrandVarCanBeSavedOnClickTab();
    this.setBrandVariablesPreview(emitedVal.typeVariable, emitedVal.typeProperty);
  }

  public closeFontMenu(event: MouseEvent): void {
    event.stopPropagation();
  }

  public emitUpdatesOnComponent() {
    this.setbrandVarCanBeSavedOnClickTab();
    this.refreshPreview();
  }

  public closeColorMenu(event) {
    if (event && !event?.target.classList.contains('iconClose')) {
      event.stopPropagation();
    }
  }

  public openFontFamilyFlyout(): void {
    if (this.menuTriggers.length) {
      this.menuTriggers.forEach((trigger) => {
        trigger.closeMenu();
      });
    }
    this.addEditCustomFont.emit();
  }

  public toggleBorderSetting(value, key) {
    if (!this.isBorderSet) {
      value.border = { width: null, color: '' };
    } else {
      value.border = { width: '1', color: '#ccccccff' };
    }
    this.brandVariablesErrors[key].border.width = '';
    this.brandVariablesErrors[key].border.color = '';
    this.emitUpdatesOnComponent();
  }
}
